import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useIntl } from "react-intl";
import PagePlaceholder from "~/assets/page_placeholder.svg";
import { PageItem } from "~/features/chat/hooks/useDocList";
import { PageStickyNotes } from "~/features/pageAnnotations/components/PageStickyNotes";
import type { PageAnnotation } from "~/features/pageAnnotations/types";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import { useSkyViewStore } from "../../../../store";
import { extractAndCopyCrop } from "../../hooks/useCropToClip";
import { useTextBoundingBoxes } from "../../hooks/useTextBoundingBoxes";
import type { TextSnipState } from "../../hooks/useTextSnip";
import { TextSnipOverlay } from "../TextSnipOverlay";
import { TextHighlightOverlay } from "./TextHighlightOverlay";
import { TruncatedSectionTitle } from "./TruncatedSectionTitle";
import { useGetUrls } from "./useGetUrls";

type DocViewerItemProps = {
  page: PageItem;
  isSelected: boolean;
  isCurrent: boolean;
  togglePageSelection: (pageId: string) => void;
  canLoad: boolean;
  isZoomActive?: boolean;
  isSnipActive?: boolean;
  organizationId?: string;
  projectId?: string;
  snipState?: TextSnipState;
  onSnipMouseDown?: (
    e: React.MouseEvent,
    containerRect: DOMRect,
    imgRect: DOMRect
  ) => void;
  onSnipMouseMove?: (e: React.MouseEvent, containerRect: DOMRect) => void;
  onSnipMouseUp?: () => void;
  onSnipHoverMove?: (e: React.MouseEvent, containerRect: DOMRect) => void;
  isCropActive?: boolean;
  annotations?: PageAnnotation[];
  onAnnotationContentChange?: (id: string, content: string) => void;
  onAnnotationColorChange?: (id: string, color: string) => void;
  onAnnotationIconChange?: (id: string, icon: string) => void;
  onAnnotationTitleChange?: (id: string, title: string) => void;
  onAnnotationPositionChange?: (
    id: string,
    positionX: number,
    positionY: number
  ) => void;
  onAnnotationDelete?: (id: string) => void;
  isViewOnly?: boolean;
  pageNumber?: number;
  isAddingAnnotation?: boolean;
  onAddAnnotationClick?: (
    pageId: string,
    positionX: number,
    positionY: number
  ) => void;
};

const DocViewerItemComponent: FC<DocViewerItemProps> = ({
  page,
  isSelected,
  isCurrent,
  togglePageSelection,
  canLoad,
  isZoomActive = false,
  isSnipActive = false,
  organizationId,
  projectId,
  snipState,
  onSnipMouseDown,
  onSnipMouseMove,
  onSnipMouseUp,
  onSnipHoverMove,
  annotations = [],
  onAnnotationContentChange,
  onAnnotationColorChange,
  onAnnotationIconChange,
  onAnnotationTitleChange,
  onAnnotationPositionChange,
  onAnnotationDelete,
  isViewOnly = false,
  pageNumber,
  isAddingAnnotation = false,
  onAddAnnotationClick,
  isCropActive = false,
}) => {
  const isSnipEnabled =
    isSelected && isSnipActive && !isZoomActive && !isAddingAnnotation;
  const isCropEnabled =
    isSelected && isCropActive && !isZoomActive && !isAddingAnnotation;

  const [crop, setCrop] = useState<Crop>();
  const { toast } = useToast();
  const intl = useIntl();

  const {
    pageId,
    getUrls,
    opensSection,
    rotate,
    width: originalWidth,
    height: originalHeight,
  } = page;

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isSnipDraggingRef = useRef(false);
  const didSnipDragRef = useRef(false);
  const snipCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      snipCleanupRef.current?.();
    };
  }, []);

  // Reset crop selection when crop mode is deactivated
  useEffect(() => {
    if (!isCropEnabled) {
      setCrop(undefined);
    }
  }, [isCropEnabled]);

  const { data: pageUrlsRotate, isFetching } = useGetUrls(
    pageId,
    getUrls,
    canLoad
  );

  let ratio = originalWidth / originalHeight;
  if (rotate % 180 === 0) {
    ratio = 1 / ratio;
  }
  const rotation = pageUrlsRotate?.rotate ?? 0;
  const scale = rotation % 180 === 0 ? 1 : ratio;

  const handleCropComplete = useCallback(
    async (c: Crop) => {
      if (!c.width || !c.height || c.width < 10 || c.height < 10) return;
      if (!imgRef.current) return;

      try {
        const copied = await extractAndCopyCrop(
          imgRef.current,
          c,
          rotation,
          scale
        );
        if (copied) {
          toast({
            title: intl.formatMessage({
              id: "docViewer.cropToClip.success.title",
              defaultMessage: "Image copied",
            }),
            description: intl.formatMessage({
              id: "docViewer.cropToClip.success.description",
              defaultMessage: "Cropped image copied to clipboard.",
            }),
          });
        } else {
          toast({
            title: intl.formatMessage({
              id: "docViewer.cropToClip.clipboardUnsupported.title",
              defaultMessage: "Image ready",
            }),
            description: intl.formatMessage({
              id: "docViewer.cropToClip.clipboardUnsupported.description",
              defaultMessage:
                "Clipboard not supported. Use the download button.",
            }),
          });
        }
      } catch {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "docViewer.cropToClip.error.title",
            defaultMessage: "Crop failed",
          }),
          description: intl.formatMessage({
            id: "docViewer.cropToClip.error.description",
            defaultMessage: "Failed to crop image. Please try again.",
          }),
        });
      }

      setCrop(undefined);
    },
    [toast, intl, rotation, scale]
  );
  const [hasRetinaError, setHasRetinaError] = useState(false);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const [rawZoomOffset, setRawZoomOffset] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);

  const searchQuery = useSkyViewStore((state) => state.searchQuery);
  const highlightedPageId = useSkyViewStore((state) => state.highlightedPageId);

  const shouldFetchBoundingBoxes =
    !!searchQuery && highlightedPageId === pageId;

  const { data: boundingBoxesData } = useTextBoundingBoxes(
    organizationId,
    projectId,
    shouldFetchBoundingBoxes ? pageId : undefined,
    shouldFetchBoundingBoxes ? searchQuery : null
  );

  const getImageUrl = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return PagePlaceholder;

    const isRetinaDisplay = window.devicePixelRatio > 1;

    if (isZoomActive || isRetinaDisplay) {
      if (!hasRetinaError && urls.retinaUrl) {
        return urls.retinaUrl;
      }
      return urls.highResUrl || urls.scanUrl || PagePlaceholder;
    }

    return urls.highResUrl || urls.scanUrl || PagePlaceholder;
  };

  const getSrcSet = () => {
    const urls = pageUrlsRotate?.urls;
    if (!urls) return undefined;

    const srcSetParts = [`${urls.highResUrl} 1x`];
    if (!hasRetinaError && urls.retinaUrl) {
      srcSetParts.push(`${urls.retinaUrl} 2x`);
    }

    return srcSetParts.length > 0 ? srcSetParts.join(", ") : undefined;
  };

  const imageUrl = getImageUrl();
  const srcSet = getSrcSet();

  const handleImageError = useCallback(() => {
    if (!hasRetinaError) {
      setHasRetinaError(true);
    }
  }, [hasRetinaError]);

  /**
   * Transform mouse coordinates accounting for rotation.
   * At 90°/270° X and Y axes are swapped.
   * At 180°/270° coordinates are inverted.
   */
  const transformCoordinatesForRotation = useCallback(
    (normalizedX: number, normalizedY: number): { x: number; y: number } => {
      const normalizedRotation = ((rotation % 360) + 360) % 360;

      switch (normalizedRotation) {
        case 90:
          return { x: normalizedY, y: 1 - normalizedX };
        case 180:
          return { x: 1 - normalizedX, y: 1 - normalizedY };
        case 270:
          return { x: 1 - normalizedY, y: normalizedX };
        default: // 0
          return { x: normalizedX, y: normalizedY };
      }
    },
    [rotation]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;

      if (isSnipDraggingRef.current) return;

      if (isSnipEnabled && onSnipHoverMove && containerRef.current) {
        onSnipHoverMove(e, containerRef.current.getBoundingClientRect());
      }

      if (!isZoomActive) return;

      const rect = containerRef.current.getBoundingClientRect();

      // Normalized position (0-1)
      const normalizedX = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width)
      );
      const normalizedY = Math.max(
        0,
        Math.min(1, (e.clientY - rect.top) / rect.height)
      );

      // Transform for rotation (used for image which has CSS rotation)
      const transformed = transformCoordinatesForRotation(
        normalizedX,
        normalizedY
      );

      // Convert to offset (-0.5 to 0.5 range, centered)
      setZoomOffset({
        x: (transformed.x - 0.5) * 100,
        y: (transformed.y - 0.5) * 100,
      });

      // Raw offset without rotation transform (used for overlay which handles rotation mathematically)
      setRawZoomOffset({
        x: (normalizedX - 0.5) * 100,
        y: (normalizedY - 0.5) * 100,
      });
    },
    [
      isZoomActive,
      isSnipEnabled,
      onSnipHoverMove,
      transformCoordinatesForRotation,
    ]
  );

  const handleMouseEnter = useCallback(() => {
    if (isZoomActive) setIsZooming(true);
  }, [isZoomActive]);

  const handleMouseLeave = useCallback(() => {
    setIsZooming(false);
    setZoomOffset({ x: 0, y: 0 });
    setRawZoomOffset({ x: 0, y: 0 });
  }, []);

  // Zoom scale factor
  const zoomScale = 2;

  // Build transform with rotation + scale + zoom offset
  const getZoomTransform = () => {
    const baseTransform = `rotate(${rotation}deg) scale(${scale})`;

    if (!isZooming || !isZoomActive) {
      return baseTransform;
    }

    // When zooming: apply zoom scale and translate based on mouse position
    return `rotate(${rotation}deg) scale(${scale * zoomScale}) translate(${-zoomOffset.x}%, ${-zoomOffset.y}%)`;
  };

  // Build transform for highlight overlay - only scale, no rotation
  // (rotation is already handled by transformBoundingBoxForRotation in TextHighlightOverlay)
  const getOverlayTransform = () => {
    const baseTransform = `scale(${scale})`;

    if (!isZooming || !isZoomActive) {
      return baseTransform;
    }

    // When zooming: apply zoom scale and translate based on raw mouse position
    // (not rotation-transformed, since overlay handles rotation mathematically)
    return `scale(${scale * zoomScale}) translate(${-rawZoomOffset.x}%, ${-rawZoomOffset.y}%)`;
  };

  function reduceCommonTextHighlights(
    matches: Array<{ box_2d: [number, number, number, number] }>
  ) {
    if (matches.length <= 2) return matches;
    const verticalGapThreshold = 15; // pixels allowed between lines to consider them mergeable
    const horizontalOverlapThreshold = 10; // pixels

    const canMerge = (
      a: { box_2d: [number, number, number, number] },
      b: { box_2d: [number, number, number, number] }
    ): boolean => {
      const [yMin1, xMin1, yMax1, xMax1] = a.box_2d;
      const [yMin2, xMin2, yMax2, xMax2] = b.box_2d;

      // Check horizontal overlap (same column)
      const horizontalOverlap = Math.max(
        0,
        Math.min(xMax1, xMax2) - Math.max(xMin1, xMin2)
      );
      if (horizontalOverlap < horizontalOverlapThreshold) return false;

      // Check vertical proximity (gap between boxes)
      const gap = Math.max(yMin1, yMin2) - Math.min(yMax1, yMax2);
      return gap <= verticalGapThreshold;
    };

    const mergeBoxes = (
      a: { box_2d: [number, number, number, number] },
      b: { box_2d: [number, number, number, number] }
    ): { box_2d: [number, number, number, number] } => {
      const [yMin1, xMin1, yMax1, xMax1] = a.box_2d;
      const [yMin2, xMin2, yMax2, xMax2] = b.box_2d;
      return {
        box_2d: [
          Math.min(yMin1, yMin2),
          Math.min(xMin1, xMin2),
          Math.max(yMax1, yMax2),
          Math.max(xMax1, xMax2),
        ],
      };
    };

    let result = [...matches];
    let merged = true;

    while (merged) {
      merged = false;
      const newResult: Array<{ box_2d: [number, number, number, number] }> = [];

      for (const current of result) {
        const mergeIndex = newResult.findIndex((existing) =>
          canMerge(existing, current)
        );

        if (mergeIndex !== -1) {
          newResult[mergeIndex] = mergeBoxes(newResult[mergeIndex], current);
          merged = true;
        } else {
          newResult.push(current);
        }
      }

      result = newResult;
    }

    return result;
  }

  return (
    <div className="flex flex-col gap-1 justify-end w-full h-full">
      {opensSection && (
        <TruncatedSectionTitle name={opensSection.sectionName} />
      )}
      <div
        ref={containerRef}
        onClick={(e) => {
          if (didSnipDragRef.current) return;
          if (snipState && snipState.status !== "idle") return;
          if (isCropEnabled) return;
          if (
            isAddingAnnotation &&
            onAddAnnotationClick &&
            containerRef.current
          ) {
            const rect = containerRef.current.getBoundingClientRect();
            const rawX = (e.clientX - rect.left) / rect.width;
            const rawY = (e.clientY - rect.top) / rect.height;
            const transformed = transformCoordinatesForRotation(rawX, rawY);
            const positionX = Math.max(0.05, Math.min(0.95, transformed.x));
            const positionY = Math.max(0.05, Math.min(0.95, transformed.y));
            onAddAnnotationClick(pageId, positionX, positionY);
            return;
          }
          togglePageSelection(pageId);
        }}
        onMouseDown={(e) => {
          didSnipDragRef.current = false;
          if (isSnipEnabled && containerRef.current && onSnipMouseDown) {
            didSnipDragRef.current = true;
            e.preventDefault();
            isSnipDraggingRef.current = true;
            const cRect = containerRef.current.getBoundingClientRect();
            const iRect = imgRef.current?.getBoundingClientRect() ?? cRect;
            onSnipMouseDown(e, cRect, iRect);

            const handleDocMouseMove = (ev: MouseEvent) => {
              if (!isSnipDraggingRef.current || !containerRef.current) return;
              didSnipDragRef.current = true;
              const moveRect = containerRef.current.getBoundingClientRect();
              onSnipMouseMove?.(ev as unknown as React.MouseEvent, moveRect);
            };

            const handleDocMouseUp = () => {
              if (isSnipDraggingRef.current) {
                isSnipDraggingRef.current = false;
                onSnipMouseUp?.();
              }
              document.removeEventListener("mousemove", handleDocMouseMove);
              document.removeEventListener("mouseup", handleDocMouseUp);
              snipCleanupRef.current = null;
            };

            document.addEventListener("mousemove", handleDocMouseMove);
            document.addEventListener("mouseup", handleDocMouseUp);
            snipCleanupRef.current = () => {
              document.removeEventListener("mousemove", handleDocMouseMove);
              document.removeEventListener("mouseup", handleDocMouseUp);
            };
          }
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={() => {}}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "relative flex justify-center items-center overflow-hidden rounded-sm h-full",
          isSnipEnabled || isCropEnabled
            ? "cursor-crosshair select-none"
            : "cursor-pointer",
          isSelected && "ring-2 ring-blue-400/70",
          isCurrent &&
            "shadow-[0px_1px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.05),0px_1px_8px_rgba(0,0,0,0.05),0px_1px_16px_rgba(0,0,0,0.05)]"
        )}
      >
        {isFetching ? (
          <div className="w-full h-full bg-muted animate-pulse" />
        ) : (
          <>
            {isCropEnabled ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => handleCropComplete(c)}
              >
                <img
                  ref={(el) => {
                    (
                      imgRef as React.MutableRefObject<HTMLImageElement | null>
                    ).current = el;
                  }}
                  src={imageUrl}
                  crossOrigin="anonymous"
                  alt={`Page ${pageId}`}
                  loading="lazy"
                  className="object-contain max-w-full max-h-full"
                  style={{
                    transform: `rotate(${rotation}deg) scale(${scale})`,
                    transformOrigin: "center center",
                  }}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onError={handleImageError}
                />
              </ReactCrop>
            ) : (
              <>
                <img
                  src={imageUrl}
                  srcSet={isZooming ? undefined : srcSet}
                  ref={(el) => {
                    (
                      imgRef as React.MutableRefObject<HTMLImageElement | null>
                    ).current = el;
                  }}
                  crossOrigin="anonymous"
                  alt={`Page ${pageId}`}
                  loading="lazy"
                  className={cn(
                    "object-contain transition-transform",
                    isZooming ? "duration-0" : "duration-300"
                  )}
                  style={{
                    transform: getZoomTransform(),
                    transformOrigin: "center center",
                  }}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onError={handleImageError}
                />
                {boundingBoxesData?.matches &&
                  boundingBoxesData.matches.length > 0 && (
                    <TextHighlightOverlay
                      matches={reduceCommonTextHighlights(
                        boundingBoxesData.matches
                      )}
                      rotation={rotation}
                      transform={getOverlayTransform()}
                      transformOrigin="center center"
                    />
                  )}
                {isSelected &&
                  isSnipActive &&
                  snipState &&
                  snipState.status !== "idle" && (
                    <TextSnipOverlay snipState={snipState} />
                  )}
              </>
            )}
            {annotations.length > 0 &&
              onAnnotationContentChange &&
              onAnnotationColorChange &&
              onAnnotationIconChange &&
              onAnnotationDelete && (
                <div className="absolute inset-0 pointer-events-none [&>*]:pointer-events-auto">
                  <PageStickyNotes
                    annotations={annotations}
                    onContentChange={onAnnotationContentChange}
                    onColorChange={onAnnotationColorChange}
                    onIconChange={onAnnotationIconChange}
                    onTitleChange={onAnnotationTitleChange}
                    onPositionChange={onAnnotationPositionChange}
                    onDelete={onAnnotationDelete}
                    isViewOnly={isViewOnly}
                    rotation={rotation}
                    pageNumber={pageNumber}
                  />
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export const DocViewerItem = memo(DocViewerItemComponent);
DocViewerItem.displayName = "DocViewerItem";
